import React, { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { api, API } from "@/lib/api";
import { THEMES } from "@/components/site/ThemeStyle";
import { RISKS, getCategoryRisks } from "@/data/risks";
import { CATEGORIES } from "@/data/categories";
import { SuppliersView, ImportCardsView } from "@/admin/SupplierModule";
import { RFQView, SalesView } from "@/admin/RFQModule";
import "@/admin/adminStyles.css";

const STAGES = ["New", "Contacted", "Qualified", "Proposal", "Agreement", "Sourcing", "Closed/Lost"];
const NAV = ["Leads", "Conversations", "Quotations", "Tour Bookings", "Orders", "Guide Downloads", "Sales", "Suppliers", "Import Cards", "Batch RFQ", "Content", "Plans", "Subscribers", "Reports", "Settings"];
const ESCALATION_RULES = [
  "Any budget, volume or timeline mention → flag HOT and call same day.",
  "Any fastener technical question (grade, coating, standard) → route to manufacturing.",
  "Any price request → hand off to a human; never quote in chat.",
  "Two consecutive unanswerable questions → flag HOT.",
];

function today() { return new Date().toISOString().slice(0, 10); }
function fmtDate(s) { return s ? new Date(s).toLocaleString() : "—"; }
function mask(p) { return p ? p.replace(/.(?=.{3})/g, "•") : ""; }

/* ---------------------------------------------------------------- Login */
function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("atlas_token", data.token);
      onLogin(data.user);
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Login failed");
    }
  };
  return (
    <div className="login-wrap">
      <form className="login-card" onSubmit={submit}>
        <h1>The Sourcing <em style={{ color: "#C08A2E", fontStyle: "normal" }}>Atlas</em></h1>
        <p style={{ color: "var(--grey)", marginBottom: 18, fontSize: 13.5 }}>Staff dashboard</p>
        <input placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" />
        <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" />
        {err && <p style={{ color: "#D62828", fontSize: 13, marginBottom: 10 }}>{err}</p>}
        <button className="btn btn-red" style={{ width: "100%" }} data-testid="admin-login-btn">Sign in</button>
      </form>
    </div>
  );
}

/* ---------------------------------------------------------------- Quotation builder */
const INCOTERMS = ["EXW", "FOB", "CIF", "CFR", "DAP", "DDP"];
const qi = { width: "100%", border: "1.5px solid var(--line)", borderRadius: 8, padding: "8px 10px", fontSize: 13, fontFamily: "inherit" };

function QuotationBuilder({ lead, existing, onClose, onSaved }) {
  const [currency, setCurrency] = useState(existing?.currency || lead.currency || "usd");
  const [items, setItems] = useState(
    existing?.totals?.line_items?.map((l) => ({ description: l.description, quantity: l.quantity, supplier_unit_price: l.supplier_unit_price }))
    || [{ description: "", quantity: 1, supplier_unit_price: 0 }]
  );
  const p0 = existing?.params || {};
  const [params, setParams] = useState({
    margin_mode: p0.margin_mode || "both", margin_pct: p0.margin_pct ?? 15, commission_amount: p0.commission_amount ?? 0,
    freight: p0.freight ?? 0, duty_pct: p0.duty_pct ?? 0, inspection_fee: p0.inspection_fee ?? 0, packaging: p0.packaging ?? 0,
    incoterm: p0.incoterm || "FOB", validity_days: p0.validity_days ?? 15, lead_time: p0.lead_time || "",
    tax_pct: p0.tax_pct ?? (currency === "inr" ? 18 : 0),
  });
  const [aiCover, setAiCover] = useState(true);
  const [fileName, setFileName] = useState(existing ? "(on record)" : "");
  const [supplierFileId, setSupplierFileId] = useState(existing?.supplier_file_id || null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(existing || null);
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");

  const setP = (k) => (e) => setParams({ ...params, [k]: e.target.value });
  const setItem = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const addRow = () => setItems([...items, { description: "", quantity: 1, supplier_unit_price: 0 }]);
  const delRow = (i) => setItems(items.filter((_, idx) => idx !== i));

  const upload = async (file) => {
    if (!file) return;
    setUploading(true); setErr(""); setFileName(file.name);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/quotations/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSupplierFileId(data.file_id);
      if (data.line_items?.length) setItems(data.line_items.map((l) => ({ description: l.description, quantity: l.quantity, supplier_unit_price: l.supplier_unit_price })));
      else setErr("AI could not read line items — please enter them manually.");
    } catch (e) { setErr("Upload/extract failed."); }
    finally { setUploading(false); }
  };

  const payload = () => ({
    lead_id: lead.id, currency, supplier_currency: currency,
    line_items: items.map((i) => ({ description: i.description, quantity: Number(i.quantity) || 0, supplier_unit_price: Number(i.supplier_unit_price) || 0 })),
    margin_mode: params.margin_mode, margin_pct: Number(params.margin_pct) || 0, commission_amount: Number(params.commission_amount) || 0,
    freight: Number(params.freight) || 0, duty_pct: Number(params.duty_pct) || 0, inspection_fee: Number(params.inspection_fee) || 0,
    packaging: Number(params.packaging) || 0, incoterm: params.incoterm, validity_days: Number(params.validity_days) || 15,
    lead_time: params.lead_time, tax_pct: Number(params.tax_pct) || 0, supplier_file_id: supplierFileId, ai_cover_note: aiCover,
  });

  const generate = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = result?.id
        ? await api.put(`/quotations/${result.id}`, payload())
        : await api.post("/quotations", payload());
      setResult(data); onSaved && onSaved();
    } catch (e) { setErr(e.response?.data?.detail || "Failed to generate quotation."); }
    finally { setBusy(false); }
  };

  const downloadPdf = () => {
    fetch(`${API}/quotations/${result.id}/pdf`, { headers: { Authorization: `Bearer ${localStorage.getItem("atlas_token")}` } })
      .then((r) => r.blob()).then((b) => window.open(URL.createObjectURL(b), "_blank"));
  };
  const sendEmail = async () => {
    setBusy(true); setErr("");
    try { const { data } = await api.post(`/quotations/${result.id}/send`, {}); setResult({ ...result, status: "sent", sent_to: data.sent_to }); onSaved && onSaved(); }
    catch (e) { setErr(e.response?.data?.detail || "Send failed (lead needs an email)."); }
    finally { setBusy(false); }
  };
  const acceptOrder = async () => {
    setBusy(true); setErr("");
    try { const { data } = await api.post(`/quotations/${result.id}/accept`, {}); setOrder(data.order); setResult({ ...result, status: "accepted", order_id: data.order.id }); onSaved && onSaved(); }
    catch (e) { setErr(e.response?.data?.detail || "Failed to convert to order."); }
    finally { setBusy(false); }
  };

  const sym = currency === "inr" ? "₹" : "$";
  const t = result?.totals;

  return (
    <div className="drawer-bg" onClick={onClose} style={{ zIndex: 400 }}>
      <div className="drawer" onClick={(e) => e.stopPropagation()} style={{ width: 680 }} data-testid="quotation-builder">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2>{result?.id ? `Quotation ${result.quote_number}` : "Generate Quotation"}</h2>
          <span style={{ cursor: "pointer", fontSize: 20 }} onClick={onClose}>✕</span>
        </div>
        <p style={{ color: "var(--grey)", fontSize: 13 }}>For {lead.name} · {lead.company}</p>

        <div className="card2">
          <b style={{ color: "var(--navy)" }}>1 · Supplier quotation</b>
          <label className="drop" style={{ cursor: "pointer", display: "block", marginTop: 8 }} data-testid="supplier-file-drop">
            <b>📎 Attach supplier PDF/image — AI extracts line items</b>
            {uploading ? "Reading…" : fileName || "PDF, JPG, PNG — up to 10 MB"}
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" style={{ display: "none" }} onChange={(e) => upload(e.target.files[0])} data-testid="supplier-file-input" />
          </label>
        </div>

        <div className="card2">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <b style={{ color: "var(--navy)" }}>2 · Line items (supplier prices)</b>
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ ...qi, width: 90 }} data-testid="quote-currency">
              <option value="usd">$ USD</option><option value="inr">₹ INR</option>
            </select>
          </div>
          <table style={{ width: "100%", marginTop: 8, fontSize: 12.5 }}>
            <thead><tr style={{ color: "var(--grey)" }}><th style={{ textAlign: "left" }}>Description</th><th>Qty</th><th>Unit ({sym})</th><th></th></tr></thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i}>
                  <td><input value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} style={qi} data-testid={`li-desc-${i}`} /></td>
                  <td style={{ width: 80 }}><input type="number" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} style={qi} data-testid={`li-qty-${i}`} /></td>
                  <td style={{ width: 100 }}><input type="number" step="0.0001" value={it.supplier_unit_price} onChange={(e) => setItem(i, "supplier_unit_price", e.target.value)} style={qi} data-testid={`li-price-${i}`} /></td>
                  <td style={{ width: 24 }}><span style={{ cursor: "pointer", color: "#D62828" }} onClick={() => delRow(i)}>✕</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="btn btn-line" style={{ marginTop: 8, padding: "6px 12px", fontSize: 12.5 }} onClick={addRow} data-testid="add-line-item">+ Add line</button>
        </div>

        <div className="card2">
          <b style={{ color: "var(--navy)" }}>3 · Key parameters</b>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 8 }}>
            <label style={lbl}>Margin mode
              <select value={params.margin_mode} onChange={setP("margin_mode")} style={qi} data-testid="margin-mode">
                <option value="pct">% markup</option><option value="fixed">Fixed commission</option><option value="both">Both</option>
              </select></label>
            <label style={lbl}>Margin %<input type="number" value={params.margin_pct} onChange={setP("margin_pct")} style={qi} data-testid="margin-pct" /></label>
            <label style={lbl}>Commission ({sym})<input type="number" value={params.commission_amount} onChange={setP("commission_amount")} style={qi} /></label>
            <label style={lbl}>Freight ({sym})<input type="number" value={params.freight} onChange={setP("freight")} style={qi} /></label>
            <label style={lbl}>Duty %<input type="number" value={params.duty_pct} onChange={setP("duty_pct")} style={qi} /></label>
            <label style={lbl}>Inspection ({sym})<input type="number" value={params.inspection_fee} onChange={setP("inspection_fee")} style={qi} /></label>
            <label style={lbl}>Packaging ({sym})<input type="number" value={params.packaging} onChange={setP("packaging")} style={qi} /></label>
            <label style={lbl}>Incoterm
              <select value={params.incoterm} onChange={setP("incoterm")} style={qi} data-testid="incoterm">{INCOTERMS.map((x) => <option key={x}>{x}</option>)}</select></label>
            <label style={lbl}>{currency === "inr" ? "GST %" : "Tax % (export=0)"}<input type="number" value={params.tax_pct} onChange={setP("tax_pct")} style={qi} data-testid="tax-pct" /></label>
            <label style={lbl}>Validity (days)<input type="number" value={params.validity_days} onChange={setP("validity_days")} style={qi} /></label>
            <label style={{ ...lbl, gridColumn: "span 2" }}>Lead time<input value={params.lead_time} onChange={setP("lead_time")} placeholder="e.g. 35 days" style={qi} /></label>
          </div>
          <label style={{ fontSize: 13, display: "block", marginTop: 8 }}><input type="checkbox" checked={aiCover} onChange={(e) => setAiCover(e.target.checked)} data-testid="ai-cover" /> AI-generate a professional cover note</label>
        </div>

        {err && <p style={{ color: "#D62828", fontSize: 13 }} data-testid="quote-error">{err}</p>}
        <button className="btn btn-red" style={{ width: "100%" }} onClick={generate} disabled={busy} data-testid="generate-quote">
          {busy ? "Working…" : result?.id ? "Recalculate quotation" : "Generate quotation"}
        </button>

        {result && t && (
          <div className="card2" style={{ marginTop: 14 }} data-testid="quote-result">
            <b style={{ color: "var(--navy)" }}>Result · {result.quote_number} <span className={"pill " + (result.status === "sent" ? "Proposal" : "New")}>{result.status}</span></b>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 10 }}>
              <div style={{ background: "#FBFDFF", border: "1px solid var(--line)", borderRadius: 8, padding: 10, fontSize: 12.5 }}>
                <div style={{ color: "var(--grey)", fontWeight: 700, marginBottom: 4 }}>INTERNAL (staff only)</div>
                <div className="row"><span>Supplier subtotal</span><b>{sym}{t.supplier_subtotal}</b></div>
                <div className="row"><span>Landed cost</span><b>{sym}{t.landed_cost}</b></div>
                <div className="row"><span>Service margin</span><b>{sym}{t.margin_amount}</b></div>
              </div>
              <div style={{ background: "#0B2649", color: "#fff", borderRadius: 8, padding: 10, fontSize: 12.5 }}>
                <div style={{ color: "#E8C67A", fontWeight: 700, marginBottom: 4 }}>CUSTOMER</div>
                <div className="row" style={{ borderColor: "#1B3A66" }}><span style={{ color: "#A9BEDD" }}>Subtotal</span><b>{sym}{t.customer_subtotal}</b></div>
                {t.tax_amount ? <div className="row" style={{ borderColor: "#1B3A66" }}><span style={{ color: "#A9BEDD" }}>GST {t.tax_pct}%</span><b>{sym}{t.tax_amount}</b></div> : null}
                <div className="row" style={{ borderColor: "#1B3A66" }}><span style={{ color: "#A9BEDD" }}>Grand total</span><b style={{ color: "#E8C67A", fontSize: 15 }}>{sym}{t.grand_total}</b></div>
              </div>
            </div>
            {result.cover_note && <p style={{ fontSize: 12.5, color: "var(--grey)", marginTop: 8, fontStyle: "italic" }}>“{result.cover_note}”</p>}
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              <button className="btn btn-navy" onClick={downloadPdf} data-testid="download-quote-pdf">View / download PDF</button>
              <button className="btn btn-line" onClick={sendEmail} disabled={busy} data-testid="send-quote">Email to customer</button>
              {result.status !== "accepted" && !result.order_id && (
                <button className="btn btn-red" onClick={acceptOrder} disabled={busy} data-testid="accept-quote">✓ Accept &amp; convert to order</button>
              )}
            </div>
            {result.sent_to && <p style={{ fontSize: 12, color: "#0E6B3C", marginTop: 6 }}>✓ Sent to {result.sent_to}</p>}
            {(order || result.order_id) && (
              <p style={{ fontSize: 12.5, color: "#0E6B3C", marginTop: 6 }} data-testid="order-confirmation">
                ✓ Converted to order {order ? order.order_number : ""} · payment due{" "}
                {order ? (currency === "inr" ? "₹" : "$") + order.amount_due : ""} via {order ? order.gateway : (currency === "inr" ? "razorpay" : "stripe")}. See the Orders tab.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- Lead drawer */
function LeadDrawer({ lead, onClose, onChange, role }) {
  const [note, setNote] = useState("");
  const [local, setLocal] = useState(lead);
  const [quotations, setQuotations] = useState([]);
  const [builder, setBuilder] = useState(null); // 'new' | quotation object
  useEffect(() => { setLocal(lead); }, [lead]);
  const loadQuotes = useCallback(() => {
    api.get(`/quotations?lead_id=${lead.id}`).then((r) => setQuotations(r.data)).catch(() => {});
  }, [lead.id]);
  useEffect(() => { loadQuotes(); }, [loadQuotes]);
  const eligible = local.source === "enquiry" || local.is_purchase_inquiry;

  const patch = async (fields) => {
    await api.patch(`/leads/${lead.id}`, fields);
    const next = { ...local, ...fields };
    setLocal(next);
    onChange();
  };
  const addNote = async () => {
    if (!note.trim()) return;
    const { data } = await api.post(`/leads/${lead.id}/notes`, { text: note });
    setLocal({ ...local, notes: [...(local.notes || []), data.note] });
    setNote("");
    onChange();
  };
  return (
    <div className="drawer-bg" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()} data-testid="lead-drawer">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2>{local.name} {local.hot && <span className="hot">🔥 HOT</span>}</h2>
          <span style={{ cursor: "pointer", fontSize: 20 }} onClick={onClose}>✕</span>
        </div>
        <p style={{ color: "var(--grey)", fontSize: 13 }}>{local.company}</p>

        <div className="card2" style={{ marginTop: 14 }}>
          <div className="row"><span>WhatsApp</span><b>{local.whatsapp || "—"}</b></div>
          <div className="row"><span>Email</span><b>{local.email || "—"}</b></div>
          <div className="row"><span>Category</span><b>{local.category || "—"}</b></div>
          <div className="row"><span>Country</span><b>{local.country || "—"}</b></div>
          <div className="row"><span>Source</span><b>{local.source} · {fmtDate(local.created_at)}</b></div>
          <div className="row"><span>Currency</span><b>{(local.currency || "—").toUpperCase()}</b></div>
        </div>

        {local.description && <div className="card2"><b style={{ color: "var(--navy)" }}>Requirement</b><p style={{ fontSize: 13.5, marginTop: 6 }}>{local.description}</p></div>}

        <div className="card2">
          <b style={{ color: "var(--navy)" }}>Stage & next action</b>
          <select className="adm-input" style={{ marginTop: 8 }} value={local.stage} onChange={(e) => patch({ stage: e.target.value })} data-testid="drawer-stage">
            {STAGES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <input type="date" className="adm-input" value={local.next_action_date || ""} onChange={(e) => patch({ next_action_date: e.target.value })} data-testid="drawer-next-date" />
          <input className="adm-input" placeholder="Next action note" defaultValue={local.next_action_note || ""} onBlur={(e) => patch({ next_action_note: e.target.value })} />
          <label style={{ fontSize: 13 }}><input type="checkbox" checked={!!local.hot} onChange={(e) => patch({ hot: e.target.checked })} /> Flag HOT</label>
          <label style={{ fontSize: 13, marginLeft: 16 }}><input type="checkbox" checked={!!local.is_purchase_inquiry} onChange={(e) => patch({ is_purchase_inquiry: e.target.checked })} data-testid="purchase-inquiry-toggle" /> Purchase inquiry</label>
        </div>

        <div className="card2" data-testid="quotations-section">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <b style={{ color: "var(--navy)" }}>Quotations</b>
            {eligible && <button className="btn btn-red" style={{ padding: "6px 12px", fontSize: 12.5 }} onClick={() => setBuilder("new")} data-testid="generate-quotation-btn">+ Generate</button>}
          </div>
          {!eligible && <p style={{ fontSize: 12.5, color: "var(--grey)", marginTop: 6 }}>Mark this lead as a <b>Purchase inquiry</b> above to build a quotation.</p>}
          {quotations.map((q) => (
            <div key={q.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px dashed var(--line)", fontSize: 13 }} data-testid={`quote-row-${q.quote_number}`}>
              <div><b>{q.quote_number}</b> <span className={"pill " + (q.status === "sent" ? "Proposal" : "New")}>{q.status}</span>
                <div style={{ color: "var(--grey)", fontSize: 11 }}>{(q.currency === "inr" ? "₹" : "$") + (q.totals?.grand_total ?? 0)} · {q.params?.incoterm}</div></div>
              <button className="btn btn-line" style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => setBuilder(q)} data-testid={`open-quote-${q.quote_number}`}>Open</button>
            </div>
          ))}
          {eligible && quotations.length === 0 && <p style={{ fontSize: 12.5, color: "var(--grey)", marginTop: 6 }}>No quotations yet.</p>}
        </div>

        {(local.files || []).length > 0 && (
          <div className="card2">
            <b style={{ color: "var(--navy)" }}>Uploaded files</b>
            {local.files.map((f) => (
              <div key={f.id} style={{ marginTop: 8 }}>
                <a className="go" style={{ color: "#2E63AE", fontWeight: 700 }} href={`${API}/files/${f.id}`} target="_blank" rel="noreferrer" data-testid="file-link"
                  onClick={(e) => { e.preventDefault(); fetch(`${API}/files/${f.id}`, { headers: { Authorization: `Bearer ${localStorage.getItem("atlas_token")}` } }).then(r => r.blob()).then(b => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = f.name; a.click(); }); }}>
                  📎 {f.name} ({Math.round((f.size || 0) / 1024)} KB)
                </a>
              </div>
            ))}
          </div>
        )}

        {(local.transcript || []).length > 0 && (
          <div className="card2">
            <b style={{ color: "var(--navy)" }}>AI chat transcript</b>
            <div className="chatlog">
              {local.transcript.map((m, i) => (
                <div key={i} className={"cm " + (m.role === "user" ? "user" : "")}>{m.text}</div>
              ))}
            </div>
          </div>
        )}

        <div className="card2">
          <b style={{ color: "var(--navy)" }}>Notes</b>
          {(local.notes || []).map((n, i) => (
            <div key={i} style={{ fontSize: 13, borderBottom: "1px dashed var(--line)", padding: "6px 0" }}>
              <div>{n.text}</div><div style={{ color: "var(--grey)", fontSize: 11 }}>{n.by} · {fmtDate(n.at)}</div>
            </div>
          ))}
          <textarea className="adm-input" placeholder="Add a note" value={note} onChange={(e) => setNote(e.target.value)} style={{ marginTop: 8, minHeight: 60 }} data-testid="note-input" />
          <button className="btn btn-navy" onClick={addNote} data-testid="note-add">Add note</button>
        </div>

        {builder && (
          <QuotationBuilder
            lead={local}
            existing={builder === "new" ? null : builder}
            onClose={() => setBuilder(null)}
            onSaved={loadQuotes}
          />
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- Leads view */
function LeadsView({ role }) {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [sel, setSel] = useState(null);
  const [f, setF] = useState({ q: "", stage: "", category: "", source: "" });

  const load = useCallback(async () => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    const { data } = await api.get("/leads", { params });
    setLeads(data);
    const s = await api.get("/admin/stats");
    setStats(s.data);
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const overdueRows = leads.filter((l) => l.next_action_date && l.next_action_date < today() && l.stage !== "Closed/Lost");

  const exportCsv = () => {
    fetch(`${API}/leads/export`, { headers: { Authorization: `Bearer ${localStorage.getItem("atlas_token")}` } })
      .then((r) => r.blob()).then((b) => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = "leads.csv"; a.click(); });
  };

  return (
    <div>
      <div className="adm-h1">Leads Inbox</div>
      <div className="adm-sub">Every enquiry from every channel, in one place.</div>
      <div className="kpis">
        <div className="kpi"><div className="n">{stats.new_today || 0}</div><div className="l">New today</div></div>
        <div className="kpi"><div className={"n" + (stats.overdue ? " red" : "")}>{stats.overdue || 0}</div><div className="l">Overdue follow-ups</div></div>
        <div className="kpi"><div className="n">{stats.active_week || 0}</div><div className="l">Active this week</div></div>
        <div className="kpi"><div className="n">{stats.proposals || 0}</div><div className="l">Proposals out</div></div>
        <div className="kpi"><div className="n">{stats.guide_downloads || 0}</div><div className="l">Guide downloads</div></div>
      </div>

      {overdueRows.length > 0 && (
        <div className="card2" style={{ borderLeft: "4px solid #D62828" }}>
          <b style={{ color: "#D62828" }}>Today's Actions — {overdueRows.length} overdue</b>
          {overdueRows.slice(0, 6).map((l) => (
            <div key={l.id} style={{ fontSize: 13, padding: "4px 0" }}>☐ <b>{l.name}</b> — {l.next_action_note || "follow up"} <span className="overdue">(due {l.next_action_date})</span></div>
          ))}
        </div>
      )}

      <div className="filterbar">
        <input placeholder="Search name, company, email…" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="lead-search" />
        <select value={f.stage} onChange={(e) => setF({ ...f, stage: e.target.value })}><option value="">All stages</option>{STAGES.map((s) => <option key={s}>{s}</option>)}</select>
        <select value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })}><option value="">All sources</option>{["callback", "enquiry", "guide", "chat"].map((s) => <option key={s}>{s}</option>)}</select>
        <button className="btn btn-navy" onClick={exportCsv} data-testid="export-csv">Export CSV</button>
      </div>

      <table className="adm-table" data-testid="leads-table">
        <thead><tr><th>Lead</th><th>Category</th><th>Country</th><th>Source</th><th>Stage</th><th>Next action</th></tr></thead>
        <tbody>
          {leads.map((l) => (
            <tr key={l.id} onClick={() => setSel(l)} data-testid={`lead-row-${l.id}`}>
              <td><b>{l.name}</b> {l.hot && <span className="hot">🔥</span>}<div style={{ color: "var(--grey)", fontSize: 12 }}>{l.company} · {mask(l.whatsapp)}</div></td>
              <td>{l.category || "—"}</td>
              <td>{l.country || "—"}</td>
              <td>{l.source}<div style={{ color: "var(--grey)", fontSize: 11 }}>{fmtDate(l.created_at)}</div></td>
              <td><span className={"pill " + (l.stage || "New").replace(/[^A-Za-z]/g, "")}>{l.stage}</span></td>
              <td>{l.next_action_date ? <span className={l.next_action_date < today() ? "overdue" : ""}>{l.next_action_date}</span> : "—"}<div style={{ color: "var(--grey)", fontSize: 11 }}>{l.next_action_note}</div></td>
            </tr>
          ))}
          {leads.length === 0 && <tr><td colSpan={6} style={{ textAlign: "center", color: "var(--grey)" }}>No leads yet.</td></tr>}
        </tbody>
      </table>

      <div className="card2" style={{ marginTop: 20 }}>
        <b style={{ color: "var(--navy)" }}>Escalation rules</b>
        <ul style={{ margin: "8px 0 0 18px", fontSize: 13, color: "var(--grey)" }}>
          {ESCALATION_RULES.map((r, i) => <li key={i} style={{ padding: "3px 0" }}>{r}</li>)}
        </ul>
      </div>

      {sel && <LeadDrawer lead={sel} role={role} onClose={() => setSel(null)} onChange={load} />}
    </div>
  );
}

/* ---------------------------------------------------------------- other views */
function openQuotationPdf(id) {
  fetch(`${API}/quotations/${id}/pdf`, { headers: { Authorization: `Bearer ${localStorage.getItem("atlas_token")}` } })
    .then((r) => (r.ok ? r.blob() : Promise.reject(new Error("PDF unavailable"))))
    .then((b) => window.open(URL.createObjectURL(b), "_blank"))
    .catch(() => alert("Could not open the quotation PDF. Please try again."));
}

function ListView({ title, sub, endpoint, columns }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(endpoint).then((r) => setRows(r.data)).catch(() => setRows([])); }, [endpoint]);
  return (
    <div>
      <div className="adm-h1">{title}</div>
      <div className="adm-sub">{sub}</div>
      <table className="adm-table">
        <thead><tr>{columns.map((c) => <th key={c.k}>{c.label}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => <tr key={i}>{columns.map((c) => <td key={c.k}>{c.render ? c.render(r) : (r[c.k] ?? "—")}</td>)}</tr>)}
          {rows.length === 0 && <tr><td colSpan={columns.length} style={{ textAlign: "center", color: "var(--grey)" }}>Nothing here yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function ReportsView() {
  const [stats, setStats] = useState({});
  const [orders, setOrders] = useState([]);
  const [subCount, setSubCount] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  useEffect(() => { api.get("/admin/newsletter/subscribers").then((r) => setSubCount(r.data.count)).catch(() => {}); }, []);
  const exportSubs = async () => {
    try {
      const r = await api.get("/admin/newsletter/subscribers.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = "newsletter_subscribers.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert("Export failed"); }
  };
  useEffect(() => {
    api.get("/orders").then((r) => setOrders((r.data || []).filter((o) => ["guide", "bundle"].includes(o.type) && o.status === "paid"))).catch(() => {});
  }, []);
  const fmt = (c, n) => (c === "inr" ? "₹" : "$") + Number(n || 0).toLocaleString();
  const Bars = ({ obj }) => (
    <div>{Object.entries(obj || {}).map(([k, v]) => (
      <div key={k} style={{ margin: "6px 0" }}>
        <div style={{ fontSize: 12.5, display: "flex", justifyContent: "space-between" }}><span>{k}</span><b>{v}</b></div>
        <div style={{ background: "#EDF4FD", borderRadius: 6, height: 8 }}><div style={{ background: "#12386E", height: 8, borderRadius: 6, width: `${Math.min(100, v * 12)}%` }} /></div>
      </div>
    ))}</div>
  );
  return (
    <div>
      <div className="adm-h1">Reports</div>
      <div className="adm-sub">Live guide sales, leads by source and conversion by stage.</div>

      <div style={{ color: "var(--navy)", fontWeight: 700, margin: "6px 0 10px" }} data-testid="sales-heading">Guide Sales</div>
      <div className="kpis" style={{ gridTemplateColumns: "repeat(4,1fr)" }}>
        <div className="kpi" data-testid="sales-revenue-inr"><div className="n">{fmt("inr", stats.revenue_inr)}</div><div className="l">Revenue (India ₹)</div></div>
        <div className="kpi" data-testid="sales-revenue-usd"><div className="n">{fmt("usd", stats.revenue_usd)}</div><div className="l">Revenue (Intl $)</div></div>
        <div className="kpi" data-testid="sales-orders"><div className="n">{stats.sales_orders ?? 0}</div><div className="l">Paid orders</div></div>
        <div className="kpi" data-testid="sales-today"><div className="n">{stats.sales_today ?? 0}</div><div className="l">Sales today</div></div>
      </div>
      <div className="card2" data-testid="recent-sales">
        <b style={{ color: "var(--navy)" }}>Recent guide sales</b>
        {orders.length === 0 ? (
          <p style={{ color: "var(--grey)", fontSize: 13, marginTop: 8 }}>No guide sales yet.</p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginTop: 10 }}>
            <thead><tr style={{ textAlign: "left", color: "var(--grey)" }}>
              <th style={{ padding: "6px 8px" }}>Order</th><th style={{ padding: "6px 8px" }}>Email</th>
              <th style={{ padding: "6px 8px" }}>Item</th><th style={{ padding: "6px 8px", textAlign: "right" }}>Amount</th>
              <th style={{ padding: "6px 8px" }}>Date</th>
            </tr></thead>
            <tbody>
              {orders.slice(0, 10).map((o) => (
                <tr key={o.order_number} style={{ borderTop: "1px solid #eef3fa" }}>
                  <td style={{ padding: "6px 8px", fontFamily: "monospace" }}>{o.order_number}</td>
                  <td style={{ padding: "6px 8px" }}>{o.email}</td>
                  <td style={{ padding: "6px 8px" }}>{o.item_slug}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700 }}>{fmt(o.currency, o.amount_total)}</td>
                  <td style={{ padding: "6px 8px", color: "var(--grey)" }}>{(o.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card2" data-testid="newsletter-card">
        <b style={{ color: "var(--navy)" }}>Newsletter subscribers</b>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 10, flexWrap: "wrap" }}>
          <div style={{ fontSize: 26, fontWeight: 800, color: "var(--navy)" }} data-testid="subscriber-count">{subCount ?? "…"}</div>
          <button className="adm-btn" onClick={exportSubs} data-testid="export-subscribers-btn"
            style={{ background: "#12386E", color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", cursor: "pointer", fontWeight: 700 }}>
            Export CSV
          </button>
        </div>
        <p style={{ fontSize: 12.5, color: "var(--grey)", marginTop: 10 }}>
          Everyone who gave an email (leads, free downloads, buyers) minus unsubscribes. Export this list to your
          email-marketing tool (Mailchimp, Brevo, your own Resend, etc.) to send newsletters — bulk sending isn't
          done through the site's transactional email.
        </p>
      </div>

      <div className="kpis" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
        <div className="kpi"><div className="n">{stats.total_leads ?? 0}</div><div className="l">Total leads</div></div>
        <div className="kpi"><div className="n">{stats.guide_downloads ?? 0}</div><div className="l">Free downloads</div></div>
        <div className="kpi"><div className="n">${stats.mrr ?? 0}</div><div className="l">MRR (est.)</div></div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div className="card2"><b style={{ color: "var(--navy)" }}>Leads by source</b><Bars obj={stats.by_source} /></div>
        <div className="card2"><b style={{ color: "var(--navy)" }}>Conversion by stage</b><Bars obj={stats.by_stage} /></div>
      </div>
    </div>
  );
}

function PlansView({ role }) {
  const [plans, setPlans] = useState([]);
  const [nw, setNw] = useState({ name: "", interval: "monthly", price_inr: 0, price_usd: 0, entitlements: "", active: false });
  const load = () => api.get("/plans?active_only=false").then((r) => setPlans(r.data));
  useEffect(() => { load(); }, []);
  if (role !== "admin") return <div><div className="adm-h1">Plans</div><div className="adm-sub">Plans are admin-only.</div></div>;
  const save = async (p) => { await api.put(`/plans/${p.id}`, { ...p, entitlements: p.entitlements }); load(); };
  const create = async () => {
    if (!nw.name) return;
    await api.post("/plans", { ...nw, entitlements: nw.entitlements.split(",").map((s) => s.trim()).filter(Boolean) });
    setNw({ name: "", interval: "monthly", price_inr: 0, price_usd: 0, entitlements: "", active: false }); load();
  };
  const del = async (id) => { await api.delete(`/plans/${id}`); load(); };
  return (
    <div>
      <div className="adm-h1">Plans</div>
      <div className="adm-sub">Membership plans (test mode — nothing on sale yet). INR→Razorpay, USD→Stripe when live.</div>
      <div className="card2">
        <b style={{ color: "var(--navy)" }}>New plan</b>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 2fr", gap: 8, marginTop: 8 }}>
          <input className="adm-input" placeholder="Name" value={nw.name} onChange={(e) => setNw({ ...nw, name: e.target.value })} data-testid="plan-name" />
          <select className="adm-input" value={nw.interval} onChange={(e) => setNw({ ...nw, interval: e.target.value })} data-testid="plan-interval"><option value="one_time">one_time</option><option value="monthly">monthly</option><option value="annual">annual</option></select>
          <input className="adm-input" type="number" placeholder="₹" value={nw.price_inr} onChange={(e) => setNw({ ...nw, price_inr: +e.target.value })} />
          <input className="adm-input" type="number" placeholder="$" value={nw.price_usd} onChange={(e) => setNw({ ...nw, price_usd: +e.target.value })} />
          <input className="adm-input" placeholder="entitlements (comma-sep)" value={nw.entitlements} onChange={(e) => setNw({ ...nw, entitlements: e.target.value })} />
        </div>
        <label style={{ fontSize: 13 }}><input type="checkbox" checked={nw.active} onChange={(e) => setNw({ ...nw, active: e.target.checked })} /> Active</label>
        <button className="btn btn-navy" style={{ marginLeft: 12 }} onClick={create} data-testid="plan-create">Add plan</button>
      </div>
      <table className="adm-table">
        <thead><tr><th>Name</th><th>Interval</th><th>₹</th><th>$</th><th>Entitlements</th><th>Active</th><th></th></tr></thead>
        <tbody>
          {plans.map((p) => (
            <tr key={p.id} data-testid={`plan-row-${p.id}`}>
              <td>{p.name}</td><td>{p.interval}</td><td>{p.price_inr}</td><td>{p.price_usd}</td>
              <td style={{ fontSize: 12 }}>{(p.entitlements || []).join(", ")}</td>
              <td><input type="checkbox" checked={p.active} onChange={(e) => save({ ...p, active: e.target.checked })} data-testid={`plan-active-${p.id}`} /></td>
              <td><span style={{ cursor: "pointer", color: "#D62828" }} onClick={() => del(p.id)}>Delete</span></td>
            </tr>
          ))}
          {plans.length === 0 && <tr><td colSpan={7} style={{ textAlign: "center", color: "var(--grey)" }}>No plans yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function SubscribersView({ role }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/subscriptions").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const cancel = async (id) => { await api.post(`/subscriptions/${id}/cancel`, {}); load(); };
  return (
    <div>
      <div className="adm-h1">Subscribers</div>
      <div className="adm-sub">Recurring subscriptions with status and next renewal (test mode).</div>
      <table className="adm-table">
        <thead><tr><th>Email</th><th>Gateway</th><th>Status</th><th>Renews</th><th></th></tr></thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id}>
              <td>{s.user_email}</td><td>{s.gateway}</td>
              <td><span className={"pill " + (s.status === "active" ? "Proposal" : s.status === "past_due" ? "Contacted" : "Closed")}>{s.status}</span></td>
              <td>{s.current_period_end || "—"}</td>
              <td>{role === "admin" && s.status !== "cancelled" && <span style={{ cursor: "pointer", color: "#D62828" }} onClick={() => cancel(s.id)} data-testid={`cancel-sub-${s.id}`}>Cancel</span>}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={5} style={{ textAlign: "center", color: "var(--grey)" }}>No subscribers yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function ContentView({ role }) {
  const [tab, setTab] = useState("Risks");
  const [risks, setRisks] = useState([]);
  const [guides, setGuides] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [cats, setCats] = useState({});
  const [home, setHome] = useState({});
  const [catSlug, setCatSlug] = useState(CATEGORIES[0].slug);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    api.get("/content").then(({ data }) => {
      const rk = data.risks?.length ? data.risks : RISKS;
      setRisks(rk.map((r) => ({ title: r[1], body: r[2], conseq: r[3] })));
      const seed = {};
      CATEGORIES.forEach((c) => {
        const ov = data.categories?.[c.slug] || {};
        seed[c.slug] = { ...c, ...ov, catRisks: (ov.catRisks && ov.catRisks.length ? ov.catRisks : getCategoryRisks(c.slug)) };
      });
      setCats(seed);
      setHome(data.home || {});
    });
    api.get("/admin/guides").then(({ data }) => { setGuides(data.guides || []); setBundles(data.bundles || []); }).catch(() => {});
  }, []);
  if (role !== "admin") return <div><div className="adm-h1">Content</div><div className="adm-sub">Content editing is admin-only.</div></div>;
  const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  const save = async () => {
    await api.put("/content", {
      risks: risks.filter((r) => r.title).map((r) => [slug(r.title), r.title, r.body, r.conseq]),
      categories: cats,
      home,
    });
    setMsg("Saved · live now"); setTimeout(() => setMsg(""), 2500);
  };
  const saveStore = async () => {
    for (const g of guides) {
      await api.put(`/admin/guides/${g.slug}`, { title: g.title, tag: g.tag, blurb: g.blurb,
        price_inr: Number(g.price_inr) || 0, price_usd: Number(g.price_usd) || 0, active: g.active !== false });
    }
    for (const b of bundles) {
      await api.put(`/admin/bundles/${b.slug}`, { name: b.name, price_inr: Number(b.price_inr) || 0, price_usd: Number(b.price_usd) || 0 });
    }
    setMsg("Store prices saved · live now"); setTimeout(() => setMsg(""), 2500);
  };
  const rset = (i, k, v) => setRisks(risks.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const gset = (i, k, v) => setGuides(guides.map((g, idx) => idx === i ? { ...g, [k]: v } : g));
  const bset = (i, k, v) => setBundles(bundles.map((b, idx) => idx === i ? { ...b, [k]: v } : b));
  const cur = cats[catSlug] || {};
  const cset = (k, v) => setCats({ ...cats, [catSlug]: { ...cur, [k]: v } });
  const hset = (k, v) => setHome({ ...home, [k]: v });
  const TABS = ["Risks", "Guides", "Categories", "Homepage"];

  return (
    <div>
      <div className="adm-h1">Content <button className="btn btn-red" style={{ float: "right", padding: "8px 16px" }} onClick={save} data-testid="content-save">Save all</button></div>
      <div className="adm-sub">Edit site copy. Changes go live immediately in the app. {msg && <b style={{ color: "#0E6B3C" }}>{msg}</b>}
        <div style={{ marginTop: 4, fontSize: 12, color: "var(--grey)" }}>Note: crawler/SEO static pages refresh on the next deploy.</div>
      </div>
      <div className="filters" style={{ margin: "10px 0 4px" }} data-testid="content-tabs">
        {TABS.map((t) => <button key={t} className={tab === t ? "on" : ""} onClick={() => setTab(t)} data-testid={`content-tab-${t.toLowerCase()}`}>{t}</button>)}
      </div>

      {tab === "Risks" && (
        <div className="card2">
          <b style={{ color: "var(--navy)" }}>Risk cards (/risks)</b>
          {risks.map((r, i) => (
            <div key={i} style={{ borderTop: "1px dashed var(--line)", padding: "10px 0" }} data-testid={`risk-edit-${i}`}>
              <input className="adm-input" value={r.title} onChange={(e) => rset(i, "title", e.target.value)} placeholder="Title (becomes a question)" />
              <textarea className="adm-input" value={r.body} onChange={(e) => rset(i, "body", e.target.value)} placeholder="Body / answer" style={{ minHeight: 54 }} />
              <input className="adm-input" value={r.conseq} onChange={(e) => rset(i, "conseq", e.target.value)} placeholder="Consequence chip" />
              <span style={{ cursor: "pointer", color: "#D62828", fontSize: 12 }} onClick={() => setRisks(risks.filter((_, x) => x !== i))}>Remove</span>
            </div>
          ))}
          <button className="btn btn-line" style={{ marginTop: 8 }} onClick={() => setRisks([...risks, { title: "", body: "", conseq: "" }])} data-testid="risk-add">+ Add risk</button>
        </div>
      )}

      {tab === "Guides" && (
        <div className="card2">
          <b style={{ color: "var(--navy)" }}>Store — guide & bundle pricing</b>
          <button className="btn btn-red" style={{ float: "right", padding: "6px 14px" }} onClick={saveStore} data-testid="store-save">Save store prices</button>
          <div style={{ fontSize: 12, color: "var(--grey)", margin: "4px 0 8px" }}>Prices are set per product, per currency. Currencies are never converted.</div>
          {guides.map((g, i) => (
            <div key={g.slug} style={{ borderTop: "1px dashed var(--line)", padding: "10px 0" }} data-testid={`guide-edit-${g.slug}`}>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 90px", gap: 6, alignItems: "center" }}>
                <input className="adm-input" value={g.title || ""} onChange={(e) => gset(i, "title", e.target.value)} placeholder="Title" />
                <select className="adm-input" value={g.tag || "free"} onChange={(e) => gset(i, "tag", e.target.value)}><option value="free">free</option><option value="paid">paid</option></select>
                <label style={{ fontSize: 12 }}><input type="checkbox" checked={g.active !== false} onChange={(e) => gset(i, "active", e.target.checked)} /> active</label>
              </div>
              {g.tag === "paid" && <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 4 }}>
                <input className="adm-input" type="number" value={g.price_inr || 0} onChange={(e) => gset(i, "price_inr", +e.target.value)} placeholder="₹ INR" data-testid={`price-inr-${g.slug}`} />
                <input className="adm-input" type="number" step="0.01" value={g.price_usd || 0} onChange={(e) => gset(i, "price_usd", +e.target.value)} placeholder="$ USD" data-testid={`price-usd-${g.slug}`} />
              </div>}
              <input className="adm-input" value={g.blurb || ""} onChange={(e) => gset(i, "blurb", e.target.value)} placeholder="Short blurb" style={{ marginTop: 4 }} />
            </div>
          ))}
          <b style={{ color: "var(--navy)", display: "block", marginTop: 16 }}>Bundles</b>
          {bundles.map((b, i) => (
            <div key={b.slug} style={{ borderTop: "1px dashed var(--line)", padding: "10px 0", display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 6 }} data-testid={`bundle-edit-${b.slug}`}>
              <input className="adm-input" value={b.name || ""} onChange={(e) => bset(i, "name", e.target.value)} placeholder="Bundle name" />
              <input className="adm-input" type="number" value={b.price_inr || 0} onChange={(e) => bset(i, "price_inr", +e.target.value)} placeholder="₹ INR" />
              <input className="adm-input" type="number" step="0.01" value={b.price_usd || 0} onChange={(e) => bset(i, "price_usd", +e.target.value)} placeholder="$ USD" />
            </div>
          ))}
        </div>
      )}

      {tab === "Categories" && (
        <div className="card2" data-testid="category-editor">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <b style={{ color: "var(--navy)" }}>Category page copy</b>
            <select className="adm-input" style={{ maxWidth: 260 }} value={catSlug} onChange={(e) => setCatSlug(e.target.value)} data-testid="category-select">
              {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
          <label className="adm-lbl">Positioning (hero lede)</label>
          <textarea className="adm-input" value={cur.positioning || ""} onChange={(e) => cset("positioning", e.target.value)} style={{ minHeight: 70 }} data-testid="cat-positioning" />
          <label className="adm-lbl">Meta description (SEO)</label>
          <textarea className="adm-input" value={cur.metaDesc || ""} onChange={(e) => cset("metaDesc", e.target.value)} style={{ minHeight: 50 }} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div><label className="adm-lbl">Product range (one per line)</label>
              <textarea className="adm-input" value={(cur.range || []).join("\n")} onChange={(e) => cset("range", e.target.value.split("\n"))} style={{ minHeight: 130 }} /></div>
            <div><label className="adm-lbl">Standards &amp; capability (one per line)</label>
              <textarea className="adm-input" value={(cur.standards || []).join("\n")} onChange={(e) => cset("standards", e.target.value.split("\n"))} style={{ minHeight: 130 }} /></div>
          </div>
          <label className="adm-lbl">Country table</label>
          {(cur.countries || []).map((row, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "0.7fr 1.2fr 1.6fr", gap: 6, marginBottom: 6 }}>
              <input className="adm-input" value={row.name || ""} onChange={(e) => cset("countries", cur.countries.map((c, idx) => idx === i ? { ...c, name: e.target.value } : c))} placeholder="Country" />
              <input className="adm-input" value={row.strength || ""} onChange={(e) => cset("countries", cur.countries.map((c, idx) => idx === i ? { ...c, strength: e.target.value } : c))} placeholder="Strength" />
              <input className="adm-input" value={row.fit || ""} onChange={(e) => cset("countries", cur.countries.map((c, idx) => idx === i ? { ...c, fit: e.target.value } : c))} placeholder="Typical fit" />
            </div>
          ))}
          <label className="adm-lbl">FAQ</label>
          {(cur.faq || []).map((f, i) => (
            <div key={i} style={{ borderTop: "1px dashed var(--line)", padding: "8px 0" }}>
              <input className="adm-input" value={f.q || ""} onChange={(e) => cset("faq", cur.faq.map((x, idx) => idx === i ? { ...x, q: e.target.value } : x))} placeholder="Question" />
              <textarea className="adm-input" value={f.a || ""} onChange={(e) => cset("faq", cur.faq.map((x, idx) => idx === i ? { ...x, a: e.target.value } : x))} placeholder="Answer" style={{ minHeight: 50 }} />
              <span style={{ cursor: "pointer", color: "#D62828", fontSize: 12 }} onClick={() => cset("faq", cur.faq.filter((_, x) => x !== i))}>Remove</span>
            </div>
          ))}
          <button className="btn btn-line" style={{ marginTop: 6 }} onClick={() => cset("faq", [...(cur.faq || []), { q: "", a: "" }])}>+ Add FAQ</button>
          <label className="adm-lbl" style={{ marginTop: 12 }}>Category risk cards (above the requirement form)</label>
          {(cur.catRisks || []).map((r, i) => (
            <div key={i} style={{ borderTop: "1px dashed var(--line)", padding: "8px 0" }}>
              <input className="adm-input" value={r[0] || ""} onChange={(e) => cset("catRisks", cur.catRisks.map((x, idx) => idx === i ? [e.target.value, x[1], x[2]] : x))} placeholder="Risk title" />
              <textarea className="adm-input" value={r[1] || ""} onChange={(e) => cset("catRisks", cur.catRisks.map((x, idx) => idx === i ? [x[0], e.target.value, x[2]] : x))} placeholder="Body" style={{ minHeight: 46 }} />
              <input className="adm-input" value={r[2] || ""} onChange={(e) => cset("catRisks", cur.catRisks.map((x, idx) => idx === i ? [x[0], x[1], e.target.value] : x))} placeholder="Consequence chip" />
              <span style={{ cursor: "pointer", color: "#D62828", fontSize: 12 }} onClick={() => cset("catRisks", cur.catRisks.filter((_, x) => x !== i))}>Remove</span>
            </div>
          ))}
          <button className="btn btn-line" style={{ marginTop: 6 }} onClick={() => cset("catRisks", [...(cur.catRisks || []), ["", "", ""]])}>+ Add category risk</button>
        </div>
      )}

      {tab === "Homepage" && (
        <div className="card2" data-testid="home-editor">
          <b style={{ color: "var(--navy)" }}>Homepage hero copy</b>
          <div style={{ fontSize: 12, color: "var(--grey)", marginBottom: 6 }}>Leave a field blank to keep the original text. Layout and design are locked.</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div><label className="adm-lbl">Headline line 1</label><input className="adm-input" value={home.heroLine1 || ""} onChange={(e) => hset("heroLine1", e.target.value)} placeholder="GLOBALIZE" data-testid="home-line1" /></div>
            <div><label className="adm-lbl">Headline line 2</label><input className="adm-input" value={home.heroLine2 || ""} onChange={(e) => hset("heroLine2", e.target.value)} placeholder="YOUR SOURCING" /></div>
          </div>
          <label className="adm-lbl">Sub-headline</label>
          <input className="adm-input" value={home.heroSub || ""} onChange={(e) => hset("heroSub", e.target.value)} placeholder="ONE PARTNER FOR 4 COUNTRIES" />
          <label className="adm-lbl">Hero paragraph</label>
          <textarea className="adm-input" value={home.heroPara || ""} onChange={(e) => hset("heroPara", e.target.value)} style={{ minHeight: 60 }} placeholder="China at factory price for Indian importers…" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div><label className="adm-lbl">Primary button label</label><input className="adm-input" value={home.heroCta1 || ""} onChange={(e) => hset("heroCta1", e.target.value)} placeholder="Get a Free Consultation" /></div>
            <div><label className="adm-lbl">Secondary button label</label><input className="adm-input" value={home.heroCta2 || ""} onChange={(e) => hset("heroCta2", e.target.value)} placeholder="Download the Free Atlas ↓" /></div>
          </div>
          <label className="adm-lbl">SEO title (browser tab & Google)</label>
          <input className="adm-input" value={home.seoTitle || ""} onChange={(e) => hset("seoTitle", e.target.value)} placeholder="China Sourcing Agent India — The Sourcing Atlas…" />
          <label className="adm-lbl">SEO description</label>
          <textarea className="adm-input" value={home.seoDesc || ""} onChange={(e) => hset("seoDesc", e.target.value)} style={{ minHeight: 50 }} />
        </div>
      )}
    </div>
  );
}

function SettingsView({ role }) {
  const [settings, setSettings] = useState({ ticker: "", whatsapp_number: "" });
  const [cohorts, setCohorts] = useState([]);
  const [msg, setMsg] = useState("");
  const load = () => {
    fetch(`${API}/settings`).then((r) => r.json()).then(setSettings);
    api.get("/cohorts").then((r) => setCohorts(r.data));
  };
  useEffect(load, []);
  if (role !== "admin") return <div><div className="adm-h1">Settings</div><div className="adm-sub">Settings are admin-only.</div></div>;

  const saveSettings = async () => { await api.put("/settings", settings); setMsg("Saved"); setTimeout(() => setMsg(""), 1500); };
  const saveCohort = async (c) => { await api.put(`/cohorts/${c.id}`, c); setMsg("Cohort saved"); setTimeout(() => setMsg(""), 1500); load(); };
  const upd = (id, k, v) => setCohorts(cohorts.map((c) => c.id === id ? { ...c, [k]: v } : c));

  return (
    <div>
      <div className="adm-h1">Settings</div>
      <div className="adm-sub">Ticker, WhatsApp number and tour cohorts (seats + prices in ₹ and $). {msg && <b style={{ color: "#0E6B3C" }}>{msg}</b>}</div>
      <div className="card2">
        <b style={{ color: "var(--navy)" }}>Site</b>
        <input className="adm-input" value={settings.ticker || ""} onChange={(e) => setSettings({ ...settings, ticker: e.target.value })} placeholder="Ticker text" data-testid="settings-ticker" />
        <input className="adm-input" value={settings.sticky_text || ""} onChange={(e) => setSettings({ ...settings, sticky_text: e.target.value })} placeholder="Sticky bar text" data-testid="settings-sticky" />
        <label style={{ fontSize: 12, color: "var(--grey)", display: "block", marginTop: 6 }}>Brand colour theme (applies site-wide)</label>
        <select className="adm-input" value={settings.theme || "navy"} onChange={(e) => setSettings({ ...settings, theme: e.target.value })} data-testid="settings-theme">
          {Object.entries(THEMES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <input className="adm-input" value={settings.whatsapp_number || ""} onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })} placeholder="WhatsApp number (+91…)" data-testid="settings-whatsapp" />
        <button className="btn btn-navy" onClick={saveSettings} data-testid="settings-save">Save</button>
      </div>
      {cohorts.map((c) => (
        <div className="card2" key={c.id}>
          <b style={{ color: "var(--navy)" }}>{c.tour} — {c.name}</b>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginTop: 8 }}>
            <label style={lbl}>Price ₹<input className="adm-input" type="number" value={c.price_inr} onChange={(e) => upd(c.id, "price_inr", +e.target.value)} data-testid={`cohort-inr-${c.id}`} /></label>
            <label style={lbl}>Price $<input className="adm-input" type="number" value={c.price_usd} onChange={(e) => upd(c.id, "price_usd", +e.target.value)} data-testid={`cohort-usd-${c.id}`} /></label>
            <label style={lbl}>Seats left<input className="adm-input" type="number" value={c.seats_left} onChange={(e) => upd(c.id, "seats_left", +e.target.value)} /></label>
            <label style={lbl}>Deposit %<input className="adm-input" type="number" value={c.deposit_pct} onChange={(e) => upd(c.id, "deposit_pct", +e.target.value)} /></label>
          </div>
          <button className="btn btn-navy" onClick={() => saveCohort(c)} data-testid={`cohort-save-${c.id}`}>Save cohort</button>
        </div>
      ))}
    </div>
  );
}
const lbl = { fontSize: 12, color: "var(--grey)", display: "block" };

/* ---------------------------------------------------------------- shell */
export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [view, setView] = useState("Leads");

  useEffect(() => {
    if (!localStorage.getItem("atlas_token")) { setUser(null); return; }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => { localStorage.removeItem("atlas_token"); setUser(null); });
  }, []);

  const logout = () => { localStorage.removeItem("atlas_token"); setUser(null); };

  if (user === undefined) return null;
  if (!user) return (<><Helmet><meta name="robots" content="noindex,nofollow" /><title>Staff Login — The Sourcing Atlas</title></Helmet><Login onLogin={setUser} /></>);

  return (
    <>
      <Helmet><meta name="robots" content="noindex,nofollow" /><title>Dashboard — The Sourcing Atlas</title></Helmet>
      <div className="adm">
        <div className="adm-side">
          <div className="brand">The Sourcing <em>Atlas</em></div>
          <nav>
            {NAV.map((n) => (
              <a key={n} className={view === n ? "on" : ""} onClick={() => setView(n)} data-testid={`nav-${n.replace(/\s/g, "-").toLowerCase()}`}><span>{n}</span></a>
            ))}
          </nav>
          <div className="foot">
            <div style={{ marginBottom: 10 }}>{user.name} · {user.role}</div>
            <button onClick={logout} data-testid="admin-logout">Sign out</button>
          </div>
        </div>
        <div className="adm-main">
          {view === "Leads" && <LeadsView role={user.role} />}
          {view === "Conversations" && <ListView title="Conversations" sub="AI chat sessions captured as leads appear in the Leads inbox with full transcript." endpoint="/leads?source=chat" columns={[{ k: "name", label: "Name" }, { k: "whatsapp", label: "WhatsApp" }, { k: "category", label: "Sourcing" }, { k: "created_at", label: "Started", render: (r) => fmtDate(r.created_at) }]} />}
          {view === "Quotations" && <ListView title="Quotations" sub="Customer quotations generated from supplier quotes. Open the lead to view or resend." endpoint="/quotations" columns={[{ k: "quote_number", label: "Quote #" }, { k: "currency", label: "Cur", render: (r) => (r.currency || "").toUpperCase() }, { k: "grand", label: "Grand total", render: (r) => (r.currency === "inr" ? "₹" : "$") + (r.totals?.grand_total ?? 0) }, { k: "status", label: "Status", render: (r) => <span className={"pill " + (r.status === "sent" ? "Proposal" : "New")}>{r.status}</span> }, { k: "created_by", label: "By" }, { k: "created_at", label: "Created", render: (r) => fmtDate(r.created_at) }, { k: "pdf", label: "PDF", render: (r) => <button className="btn btn-red" style={{ padding: "5px 11px", fontSize: 12 }} onClick={() => openQuotationPdf(r.id)} data-testid={`quotation-pdf-${r.quote_number}`}>Download</button> }]} />}
          {view === "Tour Bookings" && <ListView title="Tour Bookings" sub="Paid and pending cohort bookings." endpoint="/bookings" columns={[{ k: "name", label: "Traveller" }, { k: "cohort", label: "Cohort" }, { k: "status", label: "Status" }, { k: "gateway", label: "Gateway" }, { k: "amount_paid", label: "Paid" }]} />}
          {view === "Orders" && <ListView title="Orders" sub="Accepted quotations & digital purchases, with payment due tracking." endpoint="/orders" columns={[{ k: "order_number", label: "Order #" }, { k: "customer_name", label: "Customer", render: (r) => r.customer_name || r.email }, { k: "quote_number", label: "Quote" }, { k: "amount_due", label: "Due", render: (r) => (r.currency === "inr" ? "₹" : "$") + (r.amount_due ?? 0) }, { k: "status", label: "Status", render: (r) => <span className={"pill " + (r.status === "paid" ? "Proposal" : "New")}>{r.status}</span> }, { k: "gateway", label: "Gateway" }]} />}
          {view === "Guide Downloads" && <ListView title="Guide Downloads" sub="Email-gated free guide downloads." endpoint="/guide-downloads" columns={[{ k: "name", label: "Name" }, { k: "email", label: "Email" }, { k: "guide_slug", label: "Guide" }, { k: "created_at", label: "When", render: (r) => fmtDate(r.created_at) }]} />}
          {view === "Content" && <ContentView role={user.role} />}
          {view === "Suppliers" && <SuppliersView role={user.role} />}
          {view === "Import Cards" && <ImportCardsView />}
          {view === "Batch RFQ" && <RFQView />}
          {view === "Sales" && <SalesView />}
          {view === "Reports" && <ReportsView />}
          {view === "Plans" && <PlansView role={user.role} />}
          {view === "Subscribers" && <SubscribersView role={user.role} />}
          {view === "Settings" && <SettingsView role={user.role} />}
        </div>
      </div>
    </>
  );
}
